/**
 * Personal-data scrubber for everything this portal sends to Mixpanel.
 *
 * One Mixpanel project serves every tenant (Go Oil and Aperio) and the Aperio
 * marketing site. What analytics may hold is: the page, city, province,
 * country, the first 3 characters of the postal code, the vehicle's
 * year / make / model / engine / mileage, and non-personal ids (user id,
 * work order id, franchise id). Never a street or full address, a full postal
 * code, an email, a phone number, a person's name, a VIN, a licence plate or
 * GPS coordinates.
 *
 * Call sites are written to that rule already; this is the safety net under
 * them. `mp.track` / `mp.register` / people updates in util/analytics.ts run
 * their properties through `scrubProperties`, and the SDK's `before_send_*`
 * hooks run the final payload (super properties persisted in an older session
 * and the SDK's own `$current_url` / `$referrer` included) through
 * `scrubEventPayload` / `scrubPeoplePayload` just before it leaves the
 * browser.
 *
 * Pure functions, no Mixpanel import: see tests/analyticsScrub.spec.ts.
 */

/** Keys holding any of these words are dropped whatever their value. */
const DROP_TOKENS = new Set([
  'street',
  'address',
  'addr',
  'email',
  'phone',
  'vin',
  'plate',
  'lat',
  'lng',
  'lon',
  'latitude',
  'longitude',
  'coords',
  'coordinates',
  'geometry',
]);

/** Keys that, joined up, spell one of these are dropped too. */
const DROP_JOINED = [
  'licenseplate',
  'licenceplate',
  'placeid',
  'streetaddress',
  'emailaddress',
  'phonenumber',
];

/** `<qualifier>_name` keys naming a person (service_name, plan_name stay). */
const PERSON_NAME_QUALIFIERS = new Set([
  'first',
  'last',
  'full',
  'middle',
  'customer',
  'contact',
  'user',
  'display',
  'owner',
  'driver',
  'technician',
  'tech',
  'recipient',
  'cardholder',
  'holder',
  'billing',
  'given',
  'family',
  'sur',
]);

/** Properties whose value is a URL: sensitive query params are removed. */
const URL_KEYS = new Set([
  '$current_url',
  '$referrer',
  '$initial_referrer',
  'current_url',
  'url',
  'referrer',
  'landing_url',
  'page_url',
]);

/** Properties whose value is a raw query string (`?a=b&c=d`). */
const QUERY_KEYS = new Set(['current_url_search']);

const EMAIL_RE = /[^\s@]+@[^\s@]+\.[^\s@]+/;
// A 17-character VIN: no I, O or Q, and both letters and digits.
const VIN_RE = /^[A-HJ-NPR-Z0-9]{17}$/i;

const MAX_DEPTH = 4;

/** 'vehicleVinEntered' / 'vehicle_vin_entered' / '$first_name' -> words. */
export function keyTokens(key: string): string[] {
  return String(key)
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean);
}

/** First three characters of a postal / ZIP code, upper case, no spaces. */
export function postal3(value: unknown): string {
  return String(value ?? '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, 3);
}

function isPostalKey(tokens: string[]): boolean {
  return tokens.some((t) => t.startsWith('postal')
    || t === 'zip'
    || t.startsWith('zipcode')
    || t === 'postcode');
}

/** True when a property with this name must never reach Mixpanel. */
export function isSensitiveKey(key: string): boolean {
  const tokens = keyTokens(key);
  if (!tokens.length) return false;
  if (tokens.some((t) => DROP_TOKENS.has(t))) return true;
  const joined = tokens.join('');
  if (DROP_JOINED.some((j) => joined.includes(j))) return true;
  const last = tokens[tokens.length - 1];
  if (last === 'name' || last === 'names') {
    if (tokens.length === 1) return true;
    if (PERSON_NAME_QUALIFIERS.has(tokens[tokens.length - 2])) return true;
  }
  if (joined === 'firstname' || joined === 'lastname'
    || joined === 'fullname' || joined === 'username') {
    return true;
  }
  return false;
}

/** A string value that is personal whatever its key says. */
export function isSensitiveValue(value: unknown): boolean {
  if (typeof value !== 'string') return false;
  const v = value.trim();
  if (!v) return false;
  if (EMAIL_RE.test(v)) return true;
  return VIN_RE.test(v) && /\d/.test(v) && /[a-z]/i.test(v);
}

/** Removes (or truncates) sensitive params from a query string. */
export function scrubQueryString(search: string): string {
  const raw = String(search || '');
  const hasQ = raw.startsWith('?');
  const body = hasQ ? raw.slice(1) : raw;
  if (!body) return raw;
  const kept: string[] = [];
  body.split('&').forEach((pair) => {
    if (!pair) return;
    const eq = pair.indexOf('=');
    const rawKey = eq === -1 ? pair : pair.slice(0, eq);
    const rawVal = eq === -1 ? '' : pair.slice(eq + 1);
    let key = rawKey;
    let val = rawVal;
    try {
      key = decodeURIComponent(rawKey.replace(/\+/g, ' '));
      val = decodeURIComponent(rawVal.replace(/\+/g, ' '));
    } catch (e) {
      // malformed escape: judge the raw text
    }
    if (isSensitiveKey(key) || isSensitiveValue(val)) return;
    if (isPostalKey(keyTokens(key))) {
      kept.push(`${rawKey}=${encodeURIComponent(postal3(val))}`);
      return;
    }
    kept.push(pair);
  });
  if (!kept.length) return '';
  return `${hasQ ? '?' : ''}${kept.join('&')}`;
}

/** A URL with its sensitive query params removed; anything else untouched. */
export function scrubUrl(url: unknown): unknown {
  if (typeof url !== 'string' || !url) return url;
  // An outside shop's inspection link (/i#<token>): the fragment is the key
  // to that visit, so it never leaves the page.
  const link = /^((?:[a-z]+:\/\/[^/]*)?\/i)#/i.exec(url);
  if (link) return `${link[1]}#link`;
  const q = url.indexOf('?');
  if (q === -1) return url;
  const hash = url.indexOf('#', q);
  const base = url.slice(0, q);
  const search = hash === -1 ? url.slice(q) : url.slice(q, hash);
  const fragment = hash === -1 ? '' : url.slice(hash);
  return `${base}${scrubQueryString(search)}${fragment}`;
}

function scrubValue(value: unknown, depth: number): unknown {
  if (value === null || value === undefined) return value;
  if (typeof value === 'string') {
    return isSensitiveValue(value) ? undefined : value;
  }
  if (Array.isArray(value)) {
    if (depth >= MAX_DEPTH) return undefined;
    return value
      .map((item) => scrubValue(item, depth + 1))
      .filter((item) => item !== undefined);
  }
  if (typeof value === 'object') {
    if (depth >= MAX_DEPTH) return undefined;
    // Dates and other class instances pass through as the SDK would send them.
    if (Object.getPrototypeOf(value) !== Object.prototype
      && Object.getPrototypeOf(value) !== null) {
      return value;
    }
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    return scrubPropertiesAt(value as Record<string, unknown>, depth + 1);
  }
  return value;
}

function scrubPropertiesAt(
  props: Record<string, unknown>,
  depth: number,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  Object.keys(props).forEach((key) => {
    const value = props[key];
    if (URL_KEYS.has(key)) {
      out[key] = scrubUrl(value);
      return;
    }
    if (QUERY_KEYS.has(key)) {
      out[key] = typeof value === 'string' ? scrubQueryString(value) : value;
      return;
    }
    if (isSensitiveKey(key)) return;
    const tokens = keyTokens(key);
    if (isPostalKey(tokens)) {
      if (value === null || value === undefined || value === '') {
        out[key] = value;
      } else if (typeof value === 'string' || typeof value === 'number') {
        out[key] = postal3(value);
      }
      return;
    }
    // Mixpanel's own `$...` properties are its ids and device facts; only
    // the URL ones above can carry something personal.
    if (key.charAt(0) === '$' || key === 'distinct_id' || key === 'token') {
      out[key] = value;
      return;
    }
    const scrubbed = scrubValue(value, depth);
    if (scrubbed !== undefined || value === undefined) {
      out[key] = scrubbed;
    }
  });
  return out;
}

/** A copy of `props` with every personal property removed or reduced. */
export function scrubProperties(
  props?: Record<string, unknown> | null,
): Record<string, unknown> {
  if (!props || typeof props !== 'object') return {};
  return scrubPropertiesAt(props, 0);
}

/** `before_send_events` hook: `{ event, properties }` as the SDK queues it. */
export function scrubEventPayload<T>(data: T): T {
  if (!data || typeof data !== 'object') return data;
  const payload = data as unknown as { properties?: Record<string, unknown> };
  if (!payload.properties) return data;
  return { ...data, properties: scrubProperties(payload.properties) };
}

const PEOPLE_OPS = [
  '$set',
  '$set_once',
  '$add',
  '$append',
  '$union',
  '$remove',
  '$unset',
];

/**
 * `before_send_people` hook. A people profile gets role / brand /
 * franchise id / first booking date from util/analytics identifyUser; this
 * keeps `$name`, `$email`, `$phone` and friends out of it even if some later
 * call tries to set them.
 */
export function scrubPeoplePayload<T>(data: T): T {
  if (!data || typeof data !== 'object') return data;
  const payload = { ...(data as unknown as Record<string, unknown>) };
  PEOPLE_OPS.forEach((op) => {
    const value = payload[op];
    if (op === '$unset' && Array.isArray(value)) return;
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      payload[op] = scrubProperties(value as Record<string, unknown>);
    }
  });
  return payload as unknown as T;
}

/**
 * A distinct id this portal may identify with: the stable database user id
 * (digits), or - for the marketing-site hand-off - an opaque id. Never
 * anything that looks like an email, a phone number or a VIN.
 */
export function isSafeDistinctId(id: unknown): boolean {
  if (typeof id !== 'string' && typeof id !== 'number') return false;
  const v = String(id).trim();
  if (!v || v.length > 255) return false;
  if (isSensitiveValue(v)) return false;
  // 10+ digits with phone punctuation (+1 204-555-0100) is not a user id.
  if (/^\+?[\d\s().-]{10,}$/.test(v) && /[\s().+-]/.test(v)) return false;
  return true;
}
